import React from "react";
import { Loader2, XIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export type StrategySetActivePineDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  policyVersionLabel: string;
  hasActiveComposedPine: boolean;
  busy?: boolean;
  onConfirm: () => void | Promise<void>;
};

export function StrategySetActivePineDialog({
  open,
  onOpenChange,
  policyVersionLabel,
  hasActiveComposedPine,
  busy = false,
  onConfirm,
}: StrategySetActivePineDialogProps): React.ReactElement {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        data-strategy-set-active-pine-dialog=""
        className="sm:max-w-lg"
      >
        <DialogClose asChild>
          <button
            type="button"
            data-strategy-set-active-pine-dismiss=""
            aria-label="Close"
            className="absolute top-4 right-4 inline-flex size-8 items-center justify-center rounded-md text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <XIcon className="size-4" />
          </button>
        </DialogClose>

        <DialogHeader>
          <DialogTitle className="font-[family-name:var(--font-primary)] text-base font-normal text-[color:var(--brand-tropical-surf)]">
            Set active TradingView Pinescript?
          </DialogTitle>
          <DialogDescription className="font-[family-name:var(--font-primary)] font-normal text-gray-500 dark:text-white">
            {hasActiveComposedPine ? (
              <>
                Set{" "}
                <span className="font-medium text-[color:var(--brand-tropical-surf)]">{policyVersionLabel}</span>{" "}
                as your active TradingView Pinescript? This opens the compose flow for that policy version.
                Your current active script stays until you finish composing, or you can cancel to keep it.
              </>
            ) : (
              <>
                Set{" "}
                <span className="font-medium text-[color:var(--brand-tropical-surf)]">{policyVersionLabel}</span>{" "}
                as your active TradingView Pinescript? This opens the compose flow at step one so you can
                generate active output.
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="flex-wrap gap-2 sm:justify-end">
          <Button
            type="button"
            variant="outline-gray"
            size="sm"
            data-font="primary"
            className="h-8 shrink-0 px-3"
            disabled={busy}
            onClick={() => onOpenChange(false)}
          >
            <span className="normal-case tracking-normal">Cancel</span>
          </Button>
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="h-8 shrink-0 px-3"
            disabled={busy}
            onClick={() => void onConfirm()}
          >
            {busy ? (
              <>
                <Loader2 data-icon="inline-start" className="size-3.5 shrink-0 animate-spin" aria-hidden />
                <span className="normal-case tracking-normal">Starting…</span>
              </>
            ) : (
              <span className="normal-case tracking-normal">Yes, recompose</span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
